import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  Image,
  Drawer,
  Flex,
  Typography,
  Popover,
  List,
  Badge,
} from "antd";
import {
  MenuOutlined,
  LockOutlined,
  LogoutOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  RightOutlined,
} from "@ant-design/icons";
import Logo from "../../assets/images/logo.png";
import SidebarContent from "../Sidebar/SidebarContent";
import CustomButton from "../CustomButton/Button";
import userBlackIcon from "../../assets/icons/user-black.svg";
import notificationBlackIcon from "../../assets/icons/notification-black.svg";
import settingsIcon from "../../assets/icons/setting.svg";
import masterDataIcon from "../../assets/icons/masterdata-add-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../redux/slices/AuthSlice";
import { BTN_TXT, isRootUser } from "../../constants/constants";
import { useSocket } from "../../hooks/useSocket";
import { formatTimeAgo } from "../../helpers/helpers";
import {
  markNotificationAsRead,
  notifyMarkAsRead,
} from "../../redux/slices/NotifySlice";
import "./Navbar.scss";
import { PERM_CODE } from "../../constants/PermissionCodes";
import { capitalizeWords } from "../../helpers/helpers";

const { Header } = Layout;
const { Text, Title, Paragraph, Link } = Typography;

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const Navbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { list: docs } = useSelector(
    (state) => state.documents || { list: [] },
  );

  const { notifyList, unreadCount } = useSelector(
    (state) => state.notification,
  );
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [notifPopoverOpen, setNotifPopoverOpen] = useState(false);
  const [docsPopoverOpen, setDocsPopoverOpen] = useState(false);

  const pendingDocsCount = docs
    ? docs.filter((d) => d.status === "pending").length
    : 0;

  const privilegeCodes = user?.privilege?.map((p) => p.privilegeCode) || [];

  useSocket(user?.userId);

  const handleSidebarDrawerClose = () => setSidebarDrawerOpen(false);

  const handleLogout = async () => {
    setProfilePopoverOpen(false);
    const res = await dispatch(logoutUser());
    if (res.payload.statusCode === 201) {
      localStorage.clear();
      window.location.href = "/";
    }
  };

  const markAsRead = (notificationId) => {
    dispatch(markNotificationAsRead(notificationId)); // instantly update local state
    dispatch(notifyMarkAsRead({ notificationId, payload: { isRead: true } }));
  };

  const notifPopoverContent = (
    <div className="notif-popover-container">
      <Title className="notif-title" level={5}>Notifications</Title>
      <List
        itemLayout="horizontal"
        dataSource={notifyList.slice(0, 3)}
        locale={{ emptyText: "No notifications" }}
        renderItem={(item) => (
          <List.Item
            style={{
              backgroundColor: item.isRead ? "" : "#F5F8FF",
              cursor: "pointer",
              transition: "0.2s",
            }}
            onClick={() => {
              markAsRead(item.notificationId);
              navigate("/notifications");
              setNotifPopoverOpen(false);
            }}
          >
            <List.Item.Meta
              title={
                <Flex justify="space-between" align="center" gap={8}>
                  <Text ellipsis style={{ fontSize: 14, flex: 1, minWidth: 0 }}>
                    {item.message}
                  </Text>
                  <Text style={{ color: "#768EA7", fontSize: 10, flexShrink: 0 }}>
                    {formatTimeAgo(item.createdAt)}
                  </Text>
                </Flex>
              }
              description={
                item.redirectUrl ? (
                  <Text style={{ fontSize: 12, color: "#768EA7" }}>
                    <Link
                      style={{ fontSize: 12, textDecoration: "underline" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(item.notificationId);
                        navigate(item.redirectUrl);
                        setNotifPopoverOpen(false);
                      }}
                    >
                      {BTN_TXT.CLICK_HERE}
                    </Link>
                    {` ${BTN_TXT.TO_VIEW_DETAILS}`}
                  </Text>
                ) : (
                  <Paragraph style={{ fontSize: 12, marginBottom: 0 }} ellipsis={{ rows: 1 }}>
                    {item.description}
                  </Paragraph>
                )
              }
            />
          </List.Item>
        )}
      />
      {notifyList.length > 3 && (
        <Flex justify="center" style={{ padding: "20px 12px 12px" }}>
          <Link
            style={{ fontSize: 12 }}
            onClick={() => {
              navigate("/notifications");
              setNotifPopoverOpen(false);
            }}
          >
            {BTN_TXT.VIEW_ALL}
          </Link>
        </Flex>
      )}
    </div>
  );

  const formattedRole = capitalizeWords(user?.roleName);

  const profilePopoverContent = (
    <div className="profile-popover-card">
      <div className="profile-header">
        <div className="profile-avatar-wrapper">
          <div className="profile-avatar" data-testid="profile-avatar">
            {getInitials(user?.userName)}
          </div>
          <span className="profile-status-dot" />
        </div>
        <div className="profile-meta">
          <Text className="profile-name">{user?.userName}</Text>
          {formattedRole && (
            <span className="profile-role-badge">
              <SafetyCertificateOutlined className="role-icon" />
              <Text className="profile-role-text">{formattedRole}</Text>
            </span>
          )}
        </div>
      </div>

      {user?.email && (
        <div className="profile-email-row">
          <MailOutlined className="email-icon" />
          <Text className="profile-email">{user?.email}</Text>
        </div>
      )}

      <div className="profile-divider" />

      <div className="profile-actions">
        {!isRootUser(user) && (
          <CustomButton
            type="text"
            className="profile-action-btn"
            icon={<LockOutlined />}
            onClick={() => {
              setProfilePopoverOpen(false);
              navigate("/change-password");
            }}
          >
            <span className="menu-btn-content">
              <span className="menu-label">{BTN_TXT.CHNG_PWD}</span>
              <RightOutlined className="menu-trailing-chevron" />
            </span>
          </CustomButton>
        )}
        <CustomButton
          variant="text"
          color="danger"
          className="profile-action-btn logout-btn"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
        >
          {BTN_TXT.LOGOUT}
        </CustomButton>
      </div>
    </div>
  );

  // New Popover content for Documents
  const documentPopoverContent = (
    <div className="profile-dropdown" style={{ minWidth: 160 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "6px 0",
          cursor: "pointer",
        }}
        onClick={() => navigate("/MasterData")}
      >
        <Image src={masterDataIcon} preview={false} width={16} />
        <Text>Master Data</Text>
      </div>
    </div>
  );

  return (
    <Header className="header">
      <div className="navbar-left">
        <CustomButton
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setSidebarDrawerOpen(true)}
          className="menu-button"
        />
        <Drawer
          title={null}
          placement="left"
          onClose={handleSidebarDrawerClose}
          open={sidebarDrawerOpen}
          width="70%"
          style={{ background: "#14286D" }}
          className="custom-drawer"
        >
          <SidebarContent onClose={handleSidebarDrawerClose} />
        </Drawer>
        <Image src={Logo} alt="logo" preview={false} width={150} />
      </div>

      <div className="navbar-right">
        <Popover
          content={notifPopoverContent}
          trigger="click"
          placement="bottom"
          open={notifPopoverOpen}
          styles={{ body: { padding: 0 } }}
          onOpenChange={(value) => setNotifPopoverOpen(value)}
        >
          <Badge count={unreadCount} overflowCount={9} size="small">
            <Image
              src={notificationBlackIcon}
              alt="notification"
              preview={false}
              style={{ cursor: "pointer" }}
            />
          </Badge>
        </Popover>

        {privilegeCodes?.includes(PERM_CODE.URACC) && (
          <Popover
            content={documentPopoverContent}
            trigger="click"
            placement="bottom"
            open={docsPopoverOpen}
            onOpenChange={(value) => setDocsPopoverOpen(value)}
          >
            <div
              style={{
                cursor: "pointer",
                display: "inline-block",
                verticalAlign: "middle",
              }}
            >
              <Badge count={pendingDocsCount} overflowCount={9} size="small">
                <Image
                  src={settingsIcon}
                  alt="documents"
                  preview={false}
                  width={22}
                />
              </Badge>
            </div>
          </Popover>
        )}

        <Popover
          content={profilePopoverContent}
          trigger="click"
          placement="bottomRight"
          overlayClassName="profile-popover-overlay"
          open={profilePopoverOpen}
          onOpenChange={(value) => setProfilePopoverOpen(value)}
        >
          <div
            className={`profile-icon-trigger ${profilePopoverOpen ? "active" : ""}`}
          >
            <Image src={userBlackIcon} alt="user" preview={false} />
          </div>
        </Popover>
      </div>
    </Header>
  );
};

export default Navbar;
